import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { User } from 'src/app/_types/auth.types';
import { InsuranceFlowDetailsResponse } from 'src/app/_types/insurance-flow.types';
import { Lead } from 'src/app/_types/lead.types';
import { ProposalDetails } from 'src/app/_types/proposals.type';
import { getLeadStepFromFlowStatus } from 'src/app/enums/insurance-flow/insurance-flow-status.enum';
import { LeadType } from 'src/app/enums/lead-type.enum';
import { LanguageCode } from 'src/app/helpers/nationalities';
import { AddProposalModalComponent } from 'src/app/shared/components/add-proposal-modal/add-proposal-modal.component';
import { LeadMetadataDetailsV2Component } from 'src/app/shared/components/lead-details/metadata-components-v2/lead-metadata-details-v2/lead-metadata-details-v2.component';
import { ProposalsActionsComponent } from 'src/app/shared/components/view-proposals/proposals-actions/proposals-actions.component';
import { IconsModule } from 'src/app/shared/icons/icons.module';
import { ExpansionPanelComponent } from 'src/app/shared/ui-kit/expansion-panel/expansion-panel.component';

import { TaskDetailsService } from '../../task-details.service';
import { CentralisationHistoryComponent } from '../centralisation-history/centralisation-history.component';
import { TaskCancellationsComponent } from '../task-cancellations/task-cancellations.component';
import { TaskCancellationsService } from '../task-cancellations/task-cancellations.service';
import { TaskRunningCancellationComponent } from '../task-cancellations/task-running-cancellation.component';
import { TaskHistoryComponent } from '../task-history/task-history.component';
import { TaskLogComponent } from '../task-log/task-log.component';
import { TaskPolicyDocumentsComponent } from '../task-policy-documents/task-policy-documents.component';
import { TaskPolicyInfoComponent } from '../task-policy-info/task-policy-info.component';
import { TaskUserProfileInfoComponent } from '../task-user-profile-info/task-user-profile-info.component';

/**
 * Body of the task details view: running cancellation, history, customer
 * profile, lead metadata, offers, policy info, cancellations, contract
 * documents and activity log. Shared by the
 * task-details modal and the task-details page.
 */
@Component({
  selector: 'app-task-details-body',
  standalone: true,
  imports: [
    CommonModule,
    ExpansionPanelComponent,
    LeadMetadataDetailsV2Component,
    TaskUserProfileInfoComponent,
    TaskPolicyInfoComponent,
    TaskPolicyDocumentsComponent,
    TaskHistoryComponent,
    CentralisationHistoryComponent,
    TaskLogComponent,
    TaskCancellationsComponent,
    TaskRunningCancellationComponent,
    AddProposalModalComponent,
    ProposalsActionsComponent,
    IconsModule,
  ],
  // The affaire's cancellations, shared by the running one's cards, the cards, the documents and the log.
  providers: [TaskCancellationsService],
  templateUrl: './task-details-body.component.html',
  styleUrl: './task-details-body.component.css',
})
export class TaskDetailsBodyComponent {
  languageCode: LanguageCode =
    (this.cookieService.get('selectedCountryCode') as LanguageCode) || 'fr';

  LeadStep = LeadType;

  /** The host sheet's layer, if any; the cancellations' forms open one above it. */
  @Input() layer = 0;

  /** « Voir l'affaire » on a cancellation's replacement: the host opens that flow. */
  @Output() viewFlow = new EventEmitter<string>();

  constructor(
    private readonly taskDetailsService: TaskDetailsService,
    private readonly cookieService: CookieService,
    protected readonly cancellations: TaskCancellationsService
  ) {}

  protected get insuranceFlowData(): InsuranceFlowDetailsResponse | null {
    return this.taskDetailsService.state.insuranceFlowData;
  }

  protected get customer(): User | null {
    return this.taskDetailsService.state.userProfile;
  }

  protected get lead(): Lead | null {
    return this.taskDetailsService.state.lead;
  }

  protected get proposals(): ProposalDetails[] {
    return this.taskDetailsService.state.proposals;
  }

  protected get policy(): ProposalDetails | null {
    return this.taskDetailsService.state.policy;
  }

  protected get isLoading(): boolean {
    return this.taskDetailsService.state.isLoading;
  }

  protected get leadStep(): LeadType {
    return getLeadStepFromFlowStatus(
      this.taskDetailsService.state?.insuranceFlowData?.status
    );
  }

  protected refreshTaskDetails(): void {
    this.taskDetailsService.refreshTaskDetails();
  }
}
